/**
 * GlobalRunProgress
 *
 * Full progress bar + details panel shown on all pages EXCEPT AutomationPage
 * when a test run is active. Reuses the same RunProgressBar and RunDetailsPanel
 * components for visual consistency.
 *
 * Rendered inside main-content-card (via MainLayout) with sticky
 * positioning. Fires a toast notification when the run completes.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import {
  useIsRunActive,
  useIsRunning,
  useIsCompleted,
  useHasFailures,
  useRunProgress,
  useActiveRunMeta,
  useRunError,
  useRunState,
  useRunActions,
  useWorkerStatus,
  useRunNonce,
} from '../../store/automation/runStore';
import RunProgressBar from '../../features/automationScripts/components/RunProgressBar';
import RunDetailsPanel from '../../features/automationScripts/components/RunDetailsPanel';
import { useToastNotificationContext } from '../notifications/ToastNotificationContext';
import { resolveRunOutcome, isRunCancelled } from '../utils/permutationStatus';
import { runStreamService } from '../workers/RunStreamService';
import { useUser } from '../../features/auth/hooks/useUser';
import BatchService from '../middleware/services/BatchService';
import { extractErrorMessage } from '../utils/extractErrorMessage';
import useAutoClose from '../hooks/useAutoClose';
import logger from '../services/logging/logger';

/**
 * Build the automation page URL from run metadata.
 */
const buildRunUrl = (meta) => {
  if (!meta?.runnableFilePath) return null;
  const { projectId, runnableFilePath } = meta;
  const parts = runnableFilePath.split('/');
  const projectNameIndex = 2;
  const folderParts = parts.slice(projectNameIndex + 1, -1);
  const folderPath = folderParts.join('/');
  return `/automation-script/${projectId}/${encodeURIComponent(folderPath)}`;
};

const RunWindow = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const { userData } = useUser();
  const hasNotifiedRef = useRef(false);
  const lastMetaRef = useRef(null);

  const isRunActive = useIsRunActive();
  const isRunning = useIsRunning();
  const isCompleted = useIsCompleted();
  const hasFailures = useHasFailures();
  const { passed, failed, total, skipped, disabled } = useRunProgress();
  const activeRunMeta = useActiveRunMeta();
  const runState = useRunState();
  const workerStatus = useWorkerStatus();
  const error = useRunError();
  const { dismissRun, requestViewResults, updateActiveRunMeta } = useRunActions();

  // Keep activeRunMeta.projectName in sync with project renames so all
  // consumers of runState.projectName (panel, summary, report) stay current.
  useEffect(() => {
    const handleRename = (e) => {
      if (e.detail?.action !== 'renamed') return;
      if (!e.detail.newName) return;
      if (e.detail.projectId !== activeRunMeta?.projectId) return;
      updateActiveRunMeta({ projectDisplayName: e.detail.newName });
    };
    window.addEventListener('projectCreated', handleRename);
    return () => window.removeEventListener('projectCreated', handleRename);
  }, [activeRunMeta?.projectId, updateActiveRunMeta]);
  const { showSuccess, showWarning, showError: showErrorToast } = useToastNotificationContext();

  // Capture meta before it gets cleared on completion
  useEffect(() => {
    if (activeRunMeta) {
      lastMetaRef.current = activeRunMeta;
    }
  }, [activeRunMeta]);

  // Reset dismissed state and notification flag when a new run starts
  useEffect(() => {
    if (isRunning) {
      setDismissed(false);
      setShowDetailsPanel(false);
      hasNotifiedRef.current = false;
    }
  }, [isRunning]);

  // Fire toast notification when run completes.
  // The breakdown names skipped and disabled permutations explicitly — reporting
  // "4 passed" for a run where only 2 executed is the whole bug this guards against.
  useEffect(() => {
    if (isCompleted && !hasNotifiedRef.current) {
      hasNotifiedRef.current = true;
      const baseUrl = buildRunUrl(lastMetaRef.current) || '';
      const actionUrl = baseUrl ? `${baseUrl}?showResults=1` : '';

      // A cancelled run reports runStatus: COMPLETED. Reading out a permutation
      // tally for a run the user stopped is noise — often "0 passed, 0 failed
      // of 0" — and calling it "Completed" is wrong. Say what happened instead.
      const { isCancelled, cancelReason } =
        resolveRunOutcome(runState?.testResults?.testSummary);

      if (isCancelled) {
        showWarning('Test Run Cancelled', cancelReason || 'The run was stopped before it finished.', actionUrl);
        return;
      }

      const parts = [`${passed} passed`, `${failed} failed`];
      if (skipped > 0) parts.push(`${skipped} skipped`);
      if (disabled > 0) parts.push(`${disabled} disabled`);
      const breakdown = `${parts.join(', ')} of ${total} permutation${total === 1 ? '' : 's'}`;

      if (error) {
        showErrorToast('Test Run Failed', error, actionUrl);
      } else if (failed > 0) {
        showErrorToast('Test Run Completed with Failures', breakdown, actionUrl);
      } else {
        showSuccess('Test Run Completed', breakdown, actionUrl);
      }
    }
    // `runState` is read only at the moment of completion, so it is deliberately
    // not a dependency — adding it would re-fire the toast on every frame.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompleted, error, passed, failed, total, skipped, disabled, showSuccess, showWarning, showErrorToast]);


  const handleToggleDetails = useCallback(() => {
    setShowDetailsPanel((prev) => !prev);
  }, []);

  /**
   * Stop this run. Same endpoint a batch member uses — `POST /runs/{runId}/cancel`.
   *
   * Cancellation is cooperative: the Core notices within ~5s and a browser can
   * take up to the settle window to yield, so the button stays in a
   * "Cancelling…" state rather than claiming the run has stopped. The stream
   * reports the real terminal status when it arrives.
   */
  const handleCancelRun = useCallback(async () => {
    const meta = activeRunMeta || lastMetaRef.current;
    if (!meta?.runId || cancelling) return;

    setCancelling(true);
    setCancelError('');
    try {
      const result = await BatchService.cancelRun(meta.runId, userData);
      if (result?.alreadyTerminal > 0 && !result?.affected) {
        // Nothing to stop — it finished between the click and the request
        setCancelError('That run had already finished.');
      }
    } catch (err) {
      // A cancel that fails is exactly when the user needs telling, so this is
      // reported inline and never thrown at the page.
      logger.error('Run cancel failed', { error: err.message, runId: meta.runId });
      setCancelError(err?.response
        ? extractErrorMessage(err, 'The cancel request was rejected.')
        : 'Could not reach the service — the run is still going.');
      setCancelling(false);
    }
  }, [activeRunMeta, cancelling, userData]);

  const handleClose = useCallback(() => {
    setShowDetailsPanel(false);
    setDismissed(true);
    setCancelling(false);
    setCancelError('');
    runStreamService.disconnect();
    dismissRun();
  }, [dismissRun]);

  // A finished run closes itself after a short countdown. Any click inside the
  // window keeps it open — the user is reading it.
  const { secondsLeft: autoCloseIn, keepOpen } = useAutoClose(
    Boolean(isCompleted && runState && !dismissed), handleClose
  );

  // Whatever the run settles as — cancelled, passed, failed — it is no longer
  // cancelling. Keyed off completion rather than a timer, so a run that stops
  // sooner or later than the settle window still clears correctly.
  useEffect(() => {
    if (isCompleted) setCancelling(false);
  }, [isCompleted]);

  const handleGoToResults = useCallback(() => {
    // Signal AutomationPage to show results + select latest run (via Zustand flag)
    requestViewResults();
    // Navigate to the automation page (if not already there)
    const url = buildRunUrl(activeRunMeta || lastMetaRef.current);
    if (url) navigate(url);
  }, [activeRunMeta, navigate, requestViewResults]);

  if (!isRunActive || dismissed) return null;
  // A saved run still being checked on load has nothing to draw yet — show no
  // empty bar until it is confirmed running (or dropped)
  if (!runState && !error) return null;

  // Error banner (no progress bar)
  if (error && !runState) {
    return (
      <div className="mock-run-error">
        <AlertCircle size={14} />
        <span>{error}</span>
        <button type="button" onClick={handleClose}>&times;</button>
      </div>
    );
  }

  // A cancelled run reports runStatus: COMPLETED, so it would otherwise take the
  // green `--completed` treatment. Cancelled is neither a pass nor a failure.
  const runWasCancelled = isRunCancelled(runState?.testResults?.testSummary);

  return (
    <div
      className={[
        'mock-run-bottom',
        isRunning ? 'mock-run-bottom--running' : '',
        isCompleted && !runWasCancelled ? 'mock-run-bottom--completed' : '',
        isCompleted && runWasCancelled ? 'mock-run-bottom--cancelled' : '',
        isCompleted && hasFailures && !runWasCancelled ? 'mock-run-bottom--failed' : '',
      ].filter(Boolean).join(' ')}
      onPointerDown={keepOpen}
    >
      <RunProgressBar
        runState={runState}
        isRunning={isRunning}
        isCompleted={isCompleted}
        hasFailures={hasFailures}
        workerStatus={workerStatus}
        isDetailsOpen={showDetailsPanel}
        onToggleDetails={handleToggleDetails}
        onClose={handleClose}
        onGoToResults={handleGoToResults}
        onCancel={handleCancelRun}
        isCancelling={cancelling}
        cancelError={cancelError}
        autoCloseIn={autoCloseIn}
        onKeepOpen={keepOpen}
      />
      <RunDetailsPanel isOpen={showDetailsPanel} runState={runState} />
    </div>
  );
};

/**
 * One window per run. Keyed on the run nonce, so starting a new run closes the
 * previous run's window outright — its open details panel, cancel state,
 * auto-close countdown and pending toast — and mounts a fresh one. Nothing from
 * the old run's stream can render inside the new run's window.
 */
const GlobalRunProgress = () => {
  const runNonce = useRunNonce();
  return <RunWindow key={runNonce} />;
};

export default GlobalRunProgress;
